import { Button, Hint, Input, Label } from 'components';
import { useTranslations } from 'next-intl';
import { ChangeEvent, FC, memo, useState } from 'react';

interface ICharacterLimit {
  charColumns: string[];
  charLimit: Record<string, string>;
  errors: boolean;
  excludedColumns: string[];
  handleUpdateCharLimit: (char: string, value: string) => void;
}

const MAX_CHAR_COLUMN_SHOWN = 3;

const CharacterLimit: FC<ICharacterLimit> = ({
  charColumns,
  charLimit,
  errors,
  excludedColumns,
  handleUpdateCharLimit
}) => {
  const t = useTranslations();
  const [seeAll, setSeeAll] = useState(false);

  const handleToggleSeeAll = () => setSeeAll((oldState) => !oldState);

  const handleInputChange = (char: string) => (e: ChangeEvent<HTMLInputElement>) =>
    handleUpdateCharLimit(char, e.target.value);

  return (
    <>
      <Label>{t('ordering_form.char_limit')}</Label>
      <Hint className="mb-2">{t('ordering_form.char_limit_request_explanation_1')}</Hint>
      <Hint className="mb-2">{t('ordering_form.char_limit_request_explanation_2')}</Hint>
      <div className="ml-1">
        {charColumns.slice(0, seeAll ? charColumns.length : MAX_CHAR_COLUMN_SHOWN).map((char) => (
          <div key={`char-column-${char}`}>
            <Label>{`${t('ordering_form.column_char')}: ${char}`}</Label>
            <Input
              className={'mb-1'}
              error={errors}
              value={charLimit[char] ?? ''}
              onChange={handleInputChange(char)}
              type="number"
              min={0}
              placeholder={t('ordering_form.char_limit_of', { char })}
              disabled={excludedColumns?.includes(char)}
            />
          </div>
        ))}
      </div>
      {charColumns.length > MAX_CHAR_COLUMN_SHOWN && (
        <Button variant="text" size="small" className="!p-2" onClick={handleToggleSeeAll}>
          {seeAll ? t('ordering_form.hide_again') : t('ordering_form.see_all')}
        </Button>
      )}
    </>
  );
};

export default memo(CharacterLimit);
